/**
 * Fact resolution: turning what we know about a SKU, a market and a seller into the values
 * that `applies_when` conditions are evaluated against.
 *
 * THE CENTRAL DISTINCTION IN THIS FILE:
 *
 *   undefined / key absent  →  we do not know          →  Truth "unknown"
 *   null                    →  we know there is none   →  a real, usable answer
 *
 * These are not interchangeable and the importer must not conflate them. A CSV without a
 * `has_battery` column yields `undefined` for every row; a CSV whose `has_battery` column is
 * empty for one row is a judgement call the importer has to make deliberately, and the safe
 * direction is `undefined`. Getting this backwards re-introduces exactly the false-green bug
 * that three-valued logic exists to prevent.
 */

import type { TruthResult } from "./truth.ts";
import { unknown } from "./truth.ts";

export type FactValue = string | number | boolean | null;

/** Flat, dotted-path view of everything known. Absent key === unknown. */
export type FactBag = Readonly<Record<string, FactValue | undefined>>;

export interface FactLookup {
  /** `undefined` means unknown; `null` means known-to-be-absent. */
  readonly value: FactValue | undefined;
  /**
   * When the value is unknown, the fact paths worth asking the customer for. For a derived
   * fact this names the *underlying* input rather than the derivation, because
   * "manufacturer.establishment_country" is something a customer can answer and
   * "manufacturer.established_in_eu" is not.
   */
  readonly missing: readonly string[];
}

/**
 * EU member states, ISO 3166-1 alpha-2, as of 2026. Used only to derive establishment facts.
 *
 * This is deliberately a hard-coded constant rather than a catalog requirement: membership is
 * not a regulatory requirement, it is an input to many of them, and a stale list here would
 * silently change the output of every derived fact. Any change to it is a code change with a
 * test, not a data edit.
 */
export const EU_MEMBER_STATES: ReadonlySet<string> = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

/** Channel type prefixes that are third-party marketplaces rather than own storefronts. */
const MARKETPLACE_CHANNEL_PREFIXES = ["amazon", "bol", "etsy", "ebay", "zalando", "cdiscount", "allegro"];

/** Own online storefronts: a distance sale, but not through someone else's platform. */
const OWN_SHOP_CHANNEL_PREFIXES = ["shopify", "woocommerce", "own_shop", "webshop"];

/**
 * Regimes registered per member state whose numbers are held per market (D-070): a producer
 * registers in each member state it supplies, so one country's number never answers for another.
 * The supplied facts carry one key per market — `registration.ppwr.DE`, `registration.ppwr.FR` —
 * and a requirement names the market-relative path `registration.ppwr`, which resolves to the
 * number for the market being assessed. There is no market-less key to fill by mistake.
 */
export const PER_MARKET_REGISTRATIONS = ["ppwr", "weee", "batteries"] as const;
export type PerMarketRegistration = (typeof PER_MARKET_REGISTRATIONS)[number];

/** The supplied fact key holding one regime's number for one market. */
export function registrationFactKey(regime: PerMarketRegistration, isoCountry: string): string {
  return `registration.${regime}.${isoCountry}`;
}

/**
 * Authorised representatives, held the same way (Q-13, D-073). PPWR Art. 45(3), WEEE Art. 17(2)
 * and Batteries Regulation Art. 56(3) each want one in the member state concerned, so one
 * country's representative never answers for another. Supplied as `representative.ppwr.DE`; a
 * requirement names `representative.ppwr`.
 */
export function representativeFactKey(regime: PerMarketRegistration, isoCountry: string): string {
  return `representative.${regime}.${isoCountry}`;
}

export interface MarketContext {
  /** ISO 3166-1 alpha-2, e.g. "DE". */
  readonly iso_country: string;
}

export interface ChannelContext {
  /** e.g. "amazon_de", "shopify", "bol", "etsy", "ebay". */
  readonly type: string;
}

export interface EvaluationSubject {
  readonly facts: FactBag;
  readonly market: MarketContext;
  readonly channel?: ChannelContext;
}

/**
 * Facts computed from other facts. Each returns `undefined` when its inputs are unknown, and
 * reports the input paths so the gap list can ask for something answerable.
 */
type Derivation = (subject: EvaluationSubject) => FactLookup;

const DERIVED: Readonly<Record<string, Derivation>> = {
  "market.iso_country": (s) => known(s.market.iso_country),

  "market.jurisdiction": (s) => known(jurisdictionOf(s.market.iso_country)),

  "channel.type": (s) => (s.channel ? known(s.channel.type) : missing("channel.type")),

  /**
   * Marketplace obligations (DSA trader traceability, listing-field enforcement) attach to
   * selling through someone else's platform, not to selling online. A brand's own Shopify
   * storefront is not a marketplace and does not inherit them.
   *
   * Only a channel this file knows gets an answer. Until 23 October 2026 anything that wasn't a
   * known marketplace counted as "not a marketplace", so Kaufland, OTTO or Temu switched the DSA
   * and Italian marketplace rules to "not applicable": a false all-clear, found on 20 October
   * while writing the synthetic-data brief. A channel nobody taught this file stays unknown, as it
   * does for `channel.is_distance_sale`.
   */
  "channel.is_marketplace": (s) => {
    if (!s.channel) return missing("channel.type");
    const type = s.channel.type;
    if (MARKETPLACE_CHANNEL_PREFIXES.some((p) => type.startsWith(p))) return known(true);
    if (OWN_SHOP_CHANNEL_PREFIXES.some((p) => type.startsWith(p))) return known(false);
    return missing("channel.type");
  },

  /**
   * Selling online, through a marketplace or an own shop: a distance sale (GPSR Art. 19). Every
   * channel this product offers is one. A channel type nobody taught this file is not assumed to
   * be — it stays unknown, the same as no channel at all.
   */
  "channel.is_distance_sale": (s) => {
    if (!s.channel) return missing("channel.type");
    const type = s.channel.type;
    const online = [...MARKETPLACE_CHANNEL_PREFIXES, ...OWN_SHOP_CHANNEL_PREFIXES].some((p) => type.startsWith(p));
    return online ? known(true) : missing("channel.type");
  },

  /**
   * Where the product's manufacturer is established — in GPSR terms the company that has the
   * product made and puts its own name or trade mark on it (Art. 3), often the seller's own brand.
   * Not where the product is made: a German brand manufacturing in China is established in the
   * EU, and a UK brand manufacturing in Portugal is not. Until 18 October 2026 this was derived
   * from the country of origin, which got both cases backwards (D-070).
   */
  "manufacturer.established_in_eu": (s) =>
    inEu(s.facts["manufacturer.establishment_country"], "manufacturer.establishment_country"),

  "manufacturer.established_in_market": (s) => {
    const country = s.facts["manufacturer.establishment_country"];
    if (country === undefined || country === null) return missing("manufacturer.establishment_country");
    return known(country === s.market.iso_country);
  },

  "registration.ppwr": (s) => registrationIn(s, "ppwr"),
  "registration.weee": (s) => registrationIn(s, "weee"),
  "registration.batteries": (s) => registrationIn(s, "batteries"),

  "representative.ppwr": (s) => perMarket(s, representativeFactKey("ppwr", s.market.iso_country)),
  "representative.weee": (s) => perMarket(s, representativeFactKey("weee", s.market.iso_country)),
  "representative.batteries": (s) => perMarket(s, representativeFactKey("batteries", s.market.iso_country)),

  "organisation.established_in_eu": (s) =>
    inEu(s.facts["organisation.establishment_country"], "organisation.establishment_country"),

  /**
   * Whether the seller is established in the specific market being assessed. Drives the
   * "appoint a representative in every member state where you are not established" family of
   * obligations, which is per-market rather than per-jurisdiction.
   */
  "organisation.established_in_market": (s) => {
    const country = s.facts["organisation.establishment_country"];
    if (country === undefined) return missing("organisation.establishment_country");
    // A seller with "no" establishment country has told us something that cannot be true of a
    // business; treat it as still unknown, the same way `inEu` does, rather than as "not
    // established here", which would switch on every representative duty at once.
    if (country === null) return missing("organisation.establishment_country");
    return known(country === s.market.iso_country);
  },
};

/**
 * Every fact path a requirement may name that is not derived: the vocabulary the importer's
 * columns, the web app's forms and the catalog's `applies_when` / `required_data` all share.
 * The loader rejects a requirement that names anything else, because a misspelt path
 * (`product.is_tyo`) would otherwise validate and leave that requirement `unknown` for every
 * SKU forever, silently. A genuinely new fact is a code change here, with a test, never a
 * data edit — the same rule as `EU_MEMBER_STATES`.
 */
export const KNOWN_FACT_PATHS: ReadonlySet<string> = new Set([
  "product.sku", "product.title", "product.brand", "product.category_code", "product.gtin",
  "product.has_battery", "product.has_packaging", "product.is_electrical", "product.is_toy",
  "product.country_of_origin",

  "manufacturer.name", "manufacturer.address", "manufacturer.contact",
  "manufacturer.establishment_country",

  "organisation.establishment_country", "organisation.legal_name", "organisation.address",
  "organisation.phone", "organisation.email", "organisation.trade_register_number",
  "organisation.dsa_self_certification",
  "organisation.sells_direct_to_end_users", "organisation.annual_turnover_gbp",
  "organisation.be_packaging_kg_previous_year", "organisation.nl_packaging_kg_previous_year",
  "organisation.uk_packaging_tonnes_previous_year", "organisation.has_it_fiscal_representative",
  "organisation.uk_epr_registration_number", "organisation.uk_weee_registration_number",
  "organisation.uk_battery_registration_number", "organisation.uk_importer_name",
  "organisation.uk_importer_address",

  "packaging.lucid_number", "packaging.fr_unique_identifier", "packaging.it_conai_number",
  "packaging.nl_verpact_number", "packaging.be_ivc_number", "packaging.at_edm_number",
  "packaging.at_authorised_representative_name", "packaging.es_rpp_number",
  "packaging.es_authorised_representative_name", "packaging.ie_repak_number",
  "packaging.fr_authorised_representative_name", "packaging.it_epr_via_platform_or_conai",
  "packaging.fr_unique_identifier_displayed",

  "rp.name", "rp.address", "rp.contact", "rp.uk_name", "rp.uk_address",
]);

export function resolveFact(path: string, subject: EvaluationSubject): FactLookup {
  const derivation = DERIVED[path];
  if (derivation) return derivation(subject);

  const value = subject.facts[path];
  return value === undefined ? missing(path) : known(value);
}

/** Fact paths the evaluator can answer without being told. Used by the loader's spell-check. */
export function derivedFactPaths(): readonly string[] {
  return Object.keys(DERIVED);
}

export function jurisdictionOf(isoCountry: string): string {
  if (EU_MEMBER_STATES.has(isoCountry)) return "EU";
  if (isoCountry === "GB") return "UK";
  return "OTHER";
}

export function unknownFrom(lookup: FactLookup): TruthResult {
  return unknown(lookup.missing);
}

function known(value: FactValue): FactLookup {
  return { value, missing: [] };
}

function missing(path: string): FactLookup {
  return { value: undefined, missing: [path] };
}

function registrationIn(s: EvaluationSubject, regime: PerMarketRegistration): FactLookup {
  return perMarket(s, registrationFactKey(regime, s.market.iso_country));
}

/** A value held per market: only the key for the market being assessed can answer. */
function perMarket(s: EvaluationSubject, key: string): FactLookup {
  const value = s.facts[key];
  return value === undefined ? missing(key) : known(value);
}

function inEu(country: FactValue | undefined, sourcePath: string): FactLookup {
  if (country === undefined) return missing(sourcePath);
  // A seller who tells us they have no manufacturer country is telling us something, but not
  // something that decides EU membership. Treat it as still-unknown rather than as "not EU",
  // which would wrongly trigger the non-EU obligations.
  if (country === null) return missing(sourcePath);
  return known(typeof country === "string" && EU_MEMBER_STATES.has(country));
}
